import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Calendar,
  Clock,
  Send,
  Download,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause
} from 'lucide-react';
import { ReportTemplate } from '@/types/reports';
import ReportTemplateEditor from '@/components/reports/ReportTemplateEditor';
import ReportGenerationDashboard from '@/components/reports/ReportGenerationDashboard';
import { TrainingPlanGenerator } from '@/features/coaching/components/TrainingPlanGenerator';

const mockTemplates: ReportTemplate[] = [
  {
    id: '1',
    name: 'Weekly Player Progress',
    description: 'Comprehensive weekly performance analysis for individual players',
    type: 'player',
    frequency: 'weekly',
    recipientCount: 5,
    lastGenerated: '2024-01-14T10:00:00Z',
    nextScheduled: '2024-01-21T10:00:00Z',
    status: 'active',
    format: 'pdf',
    createdBy: 'Coach Williams',
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '2',
    name: 'Monthly Academy Report',
    description: 'Overall academy performance metrics and insights',
    type: 'academy',
    frequency: 'monthly',
    recipientCount: 12,
    lastGenerated: '2024-01-01T10:00:00Z',
    nextScheduled: '2024-02-01T10:00:00Z',
    status: 'active',
    format: 'excel',
    createdBy: 'Admin',
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '3',
    name: 'Daily Session Summary',
    description: 'Quick summary of daily training sessions',
    type: 'coach',
    frequency: 'daily',
    recipientCount: 3,
    lastGenerated: '2024-01-14T18:00:00Z',
    nextScheduled: '2024-01-15T18:00:00Z',
    status: 'paused',
    format: 'pdf',
    createdBy: 'Coach Smith',
    createdAt: '2024-01-01T00:00:00Z',
  },
];

export const AutomatedReports: React.FC = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>(mockTemplates);
  const [activeTab, setActiveTab] = useState('templates');
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplate | undefined>();
  const [showEditor, setShowEditor] = useState(false);
  const [selectedPlayerId] = useState<string>('player-1');

  const handleToggleStatus = (templateId: string) => {
    setTemplates(templates.map(template => 
      template.id === templateId
        ? { ...template, status: template.status === 'active' ? 'paused' : 'active' }
        : template
    ));
  };

  const handleDeleteTemplate = (templateId: string) => {
    setTemplates(templates.filter(template => template.id !== templateId));
  };

  const handleSaveTemplate = (template: ReportTemplate) => {
    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === template.id ? template : t));
    } else {
      setTemplates([...templates, template]);
    }
    setShowEditor(false);
    setEditingTemplate(undefined);
  };

  const handleEditTemplate = (template: ReportTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(undefined);
    setShowEditor(true);
  };

  if (showEditor) {
    return (
      <ReportTemplateEditor
        template={editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setShowEditor(false);
          setEditingTemplate(undefined);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates">Report Templates</TabsTrigger>
          <TabsTrigger value="generate">Generate Reports</TabsTrigger>
          <TabsTrigger value="training">Training Plans</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Automated Report Templates</h3>
              <p className="text-sm text-muted-foreground">
                Configure and manage recurring report templates
              </p>
            </div>
            <Button onClick={handleCreateTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          {/* Template Grid */}
          <div className="grid gap-4">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <span>{template.name}</span>
                        <Badge variant={template.status === 'active' ? 'default' : 'secondary'}>
                          {template.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>{template.description}</CardDescription>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleStatus(template.id)}
                      >
                        {template.status === 'active' ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p className="font-medium capitalize">{template.type}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Frequency</p>
                      <p className="font-medium capitalize">{template.frequency}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Recipients</p>
                      <p className="font-medium">{template.recipients} people</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Format</p>
                      <p className="font-medium uppercase">{template.format}</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>Last: {new Date(template.lastGenerated).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>Next: {new Date(template.nextScheduled).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline">
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                        <Button size="sm" variant="outline">
                          <Send className="w-3 h-3 mr-1" />
                          Send Now
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {templates.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No templates created yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first automated report template to get started
                </p>
                <Button onClick={handleCreateTemplate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="generate">
          <ReportGenerationDashboard 
            playerId={selectedPlayerId}
          />
        </TabsContent>

        <TabsContent value="training">
          <TrainingPlanGenerator
            playerId={selectedPlayerId}
            playerName="John Smith"
            currentLevel="intermediate"
            mode="quick"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};